import { memo, type FC } from 'react';

import { Outlet } from '@tanstack/react-router';

import { CookiePolicyConsent } from '@dcentralexpedientedigital/fe-ui/components/cookie-policy-consent/cookie-policy-consent';
import { Toaster } from '@dcentralexpedientedigital/fe-ui/components/shared/components/toaster/toaster';
import { Cookies } from '@dcentralexpedientedigital/fe-ui/constants/cookies';
import { useLayoutStore } from '@dcentralexpedientedigital/fe-ui/stores/layout-store';
import { getCookie } from '@dcentralexpedientedigital/fe-ui/utils/cookies';
import classNames from 'classnames';
import { useShallow } from 'zustand/react/shallow';

import { LayoutComponents, useLayout } from '../hooks/use-layout';
import { useSyncLocaleWithUrl } from '../hooks/use-sync-locale-with-url';

import { BlockRenderer } from './block-renderer';
import { BottomNav } from './bottom-nav';
import { Footer } from './footer';
import { ModalRenderer } from './modal-renderer';
import { Sidebar } from './sidebar';
import { TopNav } from './top-nav';

export const Layout: FC = memo(() => {
    const { declaration, configuration } = useLayout();
    const { variant } = declaration.props ?? { variant: undefined };
    const { maxWidth } = declaration.props ?? { maxWidth: undefined };

    const hasSideMenu = declaration.components.includes(LayoutComponents.SideNavigation);
    const hasTopNav = declaration.components.includes(LayoutComponents.TopNavigation);
    const hasBottomNav = declaration.components.includes(LayoutComponents.BottomNavigation);
    const hasFooter = declaration.components.includes(LayoutComponents.Footer);
    const isCookiePolicyAccepted = getCookie(Cookies.IsCookiePolicyAccepted) === 'true';

    useSyncLocaleWithUrl();
    const [isContentObscured] = useLayoutStore(useShallow((state) => [state.isContentObscured]));

    return (
        <main className="flex max-w-full min-h-svh">
            <ModalRenderer />
            {hasSideMenu ? (
                <Sidebar>
                    <BlockRenderer blocks={configuration.SideNavigation} />
                </Sidebar>
            ) : null}
            <div className="w-full grid grid-cols-1 grid-rows-[minmax(100svh,max-content),max-content]">
                <div
                    className={classNames('grid grid-cols-1', {
                        'grid-rows-[max-content,max-content,1fr,max-content]':
                            hasTopNav && hasBottomNav,
                        'grid-rows-[max-content,max-content,1fr]': hasTopNav && !hasBottomNav,
                        'grid-rows-[max-content,1fr,max-content]': !hasTopNav && hasBottomNav,
                        'grid-rows-[max-content,1fr]': !hasTopNav && !hasBottomNav,
                    })}
                >
                    {hasTopNav ? (
                        <TopNav>
                            <BlockRenderer blocks={configuration.TopNavigation} />
                        </TopNav>
                    ) : null}
                    <Toaster />
                    <div
                        className={classNames('relative h-full flex flex-col overflow-x-hidden', {
                            'w-full px-3 max-w-screen-xxl mx-auto pb-4': variant === 'padded',
                            'w-full px-3 pb-4 mx-auto': variant === 'contained',
                        })}
                        style={{ maxWidth: variant === 'contained' ? maxWidth : undefined }}
                    >
                        <div
                            className={classNames(
                                'absolute bg-black z-10 top-0 left-0 w-full h-full opacity-0 transition-opacity duration-300',
                                {
                                    'pointer-events-none': !isContentObscured,
                                    'opacity-60': isContentObscured,
                                },
                            )}
                        />
                        <Outlet />
                        {!isCookiePolicyAccepted && <CookiePolicyConsent />}
                    </div>

                    {hasBottomNav ? (
                        <BottomNav className="sticky bottom-0">
                            <BlockRenderer blocks={configuration.BottomNavigation} />
                        </BottomNav>
                    ) : null}
                </div>

                {hasFooter ? (
                    <Footer>
                        <BlockRenderer blocks={configuration.Footer} />
                    </Footer>
                ) : null}
            </div>
        </main>
    );
});

Layout.displayName = 'Layout';
